import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { SpaceAvatar } from "../components/SpaceAvatar";
import { ApiError, api } from "../lib/api";
import { RECOVERY_WINDOW_DAYS } from "../lib/recovery";

export function SpacesRoute() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const {
    data: spaces,
    isPending,
    error,
  } = useQuery({
    queryKey: ["spaces"],
    queryFn: () => api.listSpaces(),
  });

  const create = useMutation({
    mutationFn: (spaceName: string) => api.createSpace({ name: spaceName }),
    onSuccess: async () => {
      setName("");
      await queryClient.invalidateQueries({ queryKey: ["spaces"] });
    },
  });

  if (isPending) return <p role="status">Loading spaces…</p>;

  if (error) {
    return (
      <p role="alert" className="error">
        Could not load spaces: {error instanceof ApiError ? error.userMessage : error.message}
      </p>
    );
  }

  return (
    <section>
      <header className="page-head">
        <h1>Spaces</h1>
        <p className="muted">
          A space holds your team, your footage, and your projects. Everyone in it sees the same
          work.
        </p>
      </header>

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (trimmed) create.mutate(trimmed);
        }}
      >
        <input
          aria-label="New space name"
          placeholder="Name a new space"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={200}
        />
        <button type="submit" disabled={!name.trim() || create.isPending}>
          {create.isPending ? "Creating…" : "Create space"}
        </button>
      </form>

      {create.error && (
        <p role="alert" className="error">
          {create.error instanceof ApiError ? create.error.userMessage : create.error.message}
        </p>
      )}

      {spaces.length === 0 ? (
        <section className="empty">
          <p>No spaces yet. Create one above and it is yours to invite people into.</p>
        </section>
      ) : (
        <ul className="cards">
          {spaces.map((space) => (
            <li key={space.id}>
              <h2 className="space-card-title">
                <SpaceAvatar space={space} />
                <Link to={`/spaces/${space.id}`}>{space.name}</Link>
              </h2>
              <p className="muted">
                /{space.slug} · {space.role}
              </p>
            </li>
          ))}
        </ul>
      )}

      <RemovedSpaces />
    </section>
  );
}

/**
 * Spaces this account owns that are in the recovery window (ADR 000062).
 *
 * The one place a removed space is visible, and it renders nothing at all when
 * there are none: a permanently visible "nothing deleted" panel is a reminder
 * of a mistake nobody made.
 *
 * Only an owner is offered this, because only an owner can act on it, and the
 * API answers accordingly rather than being asked to filter here.
 */
function RemovedSpaces() {
  const queryClient = useQueryClient();

  const { data: removed } = useQuery({
    queryKey: ["spaces", "removed"],
    queryFn: () => api.listRemovedSpaces(),
  });

  const restore = useMutation({
    mutationFn: (spaceId: string) => api.restoreSpace(spaceId as never),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["spaces"] });
    },
  });

  if (!removed || removed.length === 0) return null;

  return (
    <section className="removed-spaces">
      <h2>Recently removed</h2>
      <p className="muted">
        Everything in these is still here. Put one back and it comes back whole, until the date
        below.
      </p>
      <ul className="cards">
        {removed.map((space) => (
          <li key={space.id}>
            <h3 className="space-card-title">{space.name}</h3>
            <p className="muted">
              /{space.slug} · gone for good on {goesForGood(space.deletedAt)}
            </p>
            <button
              type="button"
              onClick={() => restore.mutate(space.id)}
              disabled={restore.isPending}
            >
              Put it back
            </button>
          </li>
        ))}
      </ul>
      {restore.error && (
        <p role="alert" className="error">
          {restore.error instanceof ApiError ? restore.error.userMessage : restore.error.message}
        </p>
      )}
    </section>
  );
}

/**
 * The date a removed space stops being recoverable.
 *
 * Shown as a date rather than "30 days left", because the number somebody needs
 * is when they have to have decided by, and a countdown makes them do the
 * arithmetic to get it.
 *
 * The length itself comes from `lib/recovery` rather than a second thirty
 * written here: one window, changed in one place (ADR 000055).
 */
function goesForGood(deletedAt: string | null): string {
  if (!deletedAt) return "an unknown date";
  const deadline = new Date(deletedAt);
  deadline.setDate(deadline.getDate() + RECOVERY_WINDOW_DAYS);
  return deadline.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}
